'use client';

import { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Placeholder } from '@lmn/design-system';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { createDetailPageTitle, setPageTitle } from '@/app/common/utils/pageTitles';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { evoHeroBoxes } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { useTranslation } from '@/app/components/translations/Translation';
import EvoHeroGallery from '@/app/components/hotel-detail/evo/Hero/EvoHeroGallery';
import { EvoHeroTopBar } from '@/app/components/hotel-detail/evo/EvoHeroTopBar';
import { EvoHeroInfoPanel, EvoHeroInfoPanelSkeleton } from '@/app/components/hotel-detail/evo/Hero/EvoHeroInfoPanel';
import { EvoTopSecretGallery } from '@/app/components/hotel-detail/evo/Hero/EvoTopSecretGallery';
import TshInfoBanner from '@/app/components/hotel-detail/tsh/TSHInfoBanner';

type HeroProps = React.HTMLAttributes<HTMLElement>;

// #region styled-components
const Gallery = styled.div`
  grid-area: gallery;
  position: relative;
  padding: 0;
  height: 200px;

  @media ${customCss.breakpoints.lg} {
    height: 328px;
    flex: 1 1 50%;
    min-width: 0;
  }
`;

const HeroStyled = styled.div`
  width: 100%;
  display: grid;
  display: flex;
  flex-direction: column;

  @media ${customCss.breakpoints.lg} {
    flex-direction: row;
    border-radius: 16px;
    border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  }
`;

// #endregion

export default function Hero(props: HeroProps) {
  const { searchInfoData, searchInfoDetailData } = usePageWrapperData();
  const { t } = useTranslation();
  const { data: staticData } = useFetchHotelStatic();

  useEffect(() => {
    if (staticData?.name) {
      const detailPageTitle = createDetailPageTitle({
        hotelName: staticData.name,
        searchInfoData,
        searchInfoDetailData,
        t,
      });
      if (detailPageTitle) {
        setPageTitle(detailPageTitle);
      }
    }
  }, [staticData?.name, searchInfoData, searchInfoDetailData, t]);

  const uniqueImages = useMemo(
    () => (staticData?.images ? Array.from(new Set(staticData.images)) : []),
    [staticData?.images]
  );

  return staticData ? (
    <div {...props}>
      <EvoHeroTopBar />
      <HeroStyled data-testid="Hero">
        <Gallery>
          {/* MPH-935: a Top Secret rate is opaque, so its hero is a single blurred image behind the
              Top Secret mark and cannot be opened — see EvoTopSecretGallery. */}
          {staticData.isTopSecret ? (
            <EvoTopSecretGallery images={uniqueImages} boxes={evoHeroBoxes} />
          ) : (
            <EvoHeroGallery
              images={uniqueImages}
              name={staticData.name}
              css={css`
                width: 100%;
                height: 100%;
                flex: 1 0 auto;
              `}
            />
          )}
        </Gallery>
        <EvoHeroInfoPanel />
      </HeroStyled>
      {/* Explains why the hotel is hidden. Without it the blurred hero reads as a loading failure. */}
      {staticData.isTopSecret && (
        <TshInfoBanner
          textVariant="Body"
          css={css`
            margin-top: 16px;

            @media ${customCss.breakpoints.lg} {
              border-radius: 8px;
            }
          `}
        />
      )}
    </div>
  ) : (
    <HeroSkeleton {...props} />
  );
}

function HeroSkeleton(props: HeroProps) {
  return (
    <div {...props}>
      <EvoHeroTopBar />
      <HeroStyled data-testid="Hero">
        <GallerySkeleton />
        <EvoHeroInfoPanelSkeleton />
      </HeroStyled>
    </div>
  );
}

// Clips the skeleton placeholder to the card's rounded left corners (the loaded
// image rounds its own corners in EvoHeroGalleryImageSlide; HeroStyled itself
// has no overflow:hidden, so the square placeholder would otherwise overflow).
const GallerySkeletonClip = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media ${customCss.breakpoints.lg} {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }
`;

function GallerySkeleton() {
  return (
    <Gallery>
      <GallerySkeletonClip>
        <Placeholder height="100%" width="100%" />
      </GallerySkeletonClip>
    </Gallery>
  );
}
